import { ArchivedFilter } from "@avenueops/shared-types";
import { PopoverContent, Popover, PopoverTrigger, SmartForm, Button } from "@avenueops/web-components";
import { EllipsisVerticalIcon, CloudArrowDownIcon } from "@heroicons/react/24/outline";
import { zodResolver } from "@hookform/resolvers/zod";
import GeneralModal from "components/GeneralModal";
import EmailMultiInput from "components/inputs/EmailMultiInput";
import { ProcessTableStateType } from "context/ProcessTableContext";
import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { trpc } from "utils/trpc";
import { z } from "zod";

const DownloadCsvInput = z.object({
  emails: z.string().email({ message: "Invalid email address" }).array().min(1, { message: "Please provide at least one email address." }),
  filteredColumnsRowsImpacted: z.array(z.string()).optional(),
});

type DownloadCsvInput = z.infer<typeof DownloadCsvInput>;

interface Props {
  processTableState: ProcessTableStateType;
  workflowColumns?: string[];
  archived: ArchivedFilter;
  isDashboard: boolean;
}

function CsvDownloadModal({
  showModal,
  setShowModal,
  processTableState,
  workflowColumns,
  archived,
  isDashboard,
}: {
  showModal: boolean;
  setShowModal: (showModal: boolean) => void;
  processTableState: ProcessTableStateType;
  workflowColumns?: string[];
  archived: ArchivedFilter;
  isDashboard: boolean;
}) {
  // Props
  const hideModal = () => setShowModal(false);
  const defaultValues = { emails: [], filteredColumnsRowsImpacted: [] };

  // Form Hooks
  const formMethods = useForm({
    defaultValues,
    shouldUnregister: true,
    mode: "onChange",
    resolver: zodResolver(DownloadCsvInput),
  });
  const {
    handleSubmit,
    getValues,
    control,
    formState: { errors, isSubmitting, isDirty },
  } = formMethods;

  const { mutateAsync, isLoading } = trpc.processes.csvReportDownload.useMutation({
    onSuccess: async () => {
      hideModal();
      toast.success(`Report was successfully sent to ${getValues().emails.join(", ")}`);
    },
  });

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const onSubmit = async (data: any, event: any) => {
    try {
      await mutateAsync({ ...data, ...processTableState, timeZone, archived, isDashboard });
    } catch (err) {
      toast.error(`There was an error sending your CSV report. Please try again later.`);
    }
  };

  return (
    <FormProvider {...formMethods}>
      <GeneralModal
        isLoading={isSubmitting}
        modalVisible={showModal}
        setModalVisible={setShowModal}
        title="Download Signals as CSV"
        description="Send a summary of the signals in this filtered view as a CSV report to an email."
        confirmText="Send Report"
        onConfirm={handleSubmit(onSubmit, (errors) => {
          throw new Error("Invalid Form Values");
        })}
        disableConfirm={!isDirty || isSubmitting}
      >
        <div className="flex flex-col gap-4">
          <EmailMultiInput title="Email Address" required={true} name="emails" control={control} options={[]} />
          <SmartForm.DropdownMultiSelect
            title="Filter Columns for Rows Impacted (Optional)"
            options={
              workflowColumns?.map((column) => {
                return { label: column, value: column };
              }) ?? []
            }
            onCreate={(item) => {
              return { value: item, label: item };
            }}
            name="filteredColumnsRowsImpacted"
          />
        </div>
      </GeneralModal>
    </FormProvider>
  );
}

export default function DownloadAsCsvDropdown({ workflowColumns, archived, processTableState, isDashboard }: Props) {
  const [showModal, setShowModal] = React.useState(false);

  return (
    <>
      <CsvDownloadModal
        showModal={showModal}
        setShowModal={setShowModal}
        processTableState={processTableState}
        workflowColumns={workflowColumns}
        archived={archived}
        isDashboard={isDashboard}
      />
      <Button LeadIcon={CloudArrowDownIcon} type="SECONDARY" size="MEDIUM" onClick={() => setShowModal(true)} />
    </>
  );
}
